import { Button } from "@ui/Button";
import { useLayoutAppKind } from "@ui/layouts/layoutProvider";
import { toast } from "@ui/molecules/Toasts/toast";
import { Panel } from "@ui/Panel";
import { PanelFooter } from "@ui/PanelFooter";
import {
	type Accessor,
	createEffect,
	createSignal,
	Match,
	Switch,
} from "solid-js";
import { createStore } from "solid-js/store";
import { AddBoardForm } from "~/modules/askgvt/boards/list/components/AddBoardForm";
import type { AddBoardFormData } from "~/modules/askgvt/boards/list/components/NewBoardButton";
import type { BoardInfo } from "./addToBoardSave";
import { BoardList } from "./boardList/BoardList";
import { boardsListQueryFor, useBoardsList } from "./boardsContext";
import { createBoardForContext } from "./createBoardForContext";
import { NewBoardButton } from "./NewBoardButton";

const EMPTY_FORM: AddBoardFormData = {
	title: "",
	description: "",
	privacy: "private",
	icon: "",
};

type Props = {
	isOpen: Accessor<boolean>;
	setIsOpen: (isOpen: boolean) => void;
	title: string;
	confirmLabel: string;
	projectId?: string;
	/** Runs against the chosen (or newly created) board; the panel closes once it resolves. */
	onConfirm: (board: BoardInfo & { id: string }) => Promise<void>;
};

/**
 * Slide-over board picker with no clip attached, for callers that add something
 * other than a single trimmed clip to a board.
 */
export const SelectBoardPanel = (props: Props) => {
	const appKind = useLayoutAppKind();
	const showPrivacy = appKind === "askgvt" || appKind === "studio";

	const [selectedBoardId, setSelectedBoardId] = createSignal<string | null>(
		null,
	);
	const [currentPanel, setCurrentPanel] = createSignal(0);
	const [isSaving, setIsSaving] = createSignal(false);
	const [formData, setFormData] = createStore<AddBoardFormData>({
		...EMPTY_FORM,
	});
	const [errors, setErrors] = createSignal<Record<string, string[]>>({});

	const boardsList = useBoardsList(() =>
		boardsListQueryFor({ appKind, projectId: props.projectId }),
	);

	createEffect(() => {
		if (props.isOpen()) {
			return;
		}
		setSelectedBoardId(null);
		setCurrentPanel(0);
		setFormData({ ...EMPTY_FORM });
		setErrors({});
	});

	const confirm = async (board: BoardInfo & { id: string }) => {
		setIsSaving(true);
		try {
			await props.onConfirm(board);
		} finally {
			setIsSaving(false);
		}
		props.setIsOpen(false);
	};

	const confirmSelected = async () => {
		const boardId = selectedBoardId();
		if (!boardId) {
			return;
		}
		const board = boardsList()?.boards?.find((entry) => entry.id === boardId);
		await confirm({ id: boardId, title: board?.title ?? null });
	};

	const createAndConfirm = async () => {
		setIsSaving(true);
		const created = await createBoardForContext({
			formData,
			appKind,
			projectId: props.projectId,
		});
		setIsSaving(false);
		if (created.ok === false) {
			if (created.errors) {
				setErrors(created.errors);
			} else {
				toast.error({ title: "Failed to create board" });
			}
			return;
		}
		await confirm(created.board);
	};

	return (
		<Panel
			isOpen={props.isOpen}
			onToggle={() => props.setIsOpen(!props.isOpen())}
			title={currentPanel() === 1 ? "New board" : props.title}
			symbol="bookmark_add"
			class="select-board-panel"
			footer={
				<Switch>
					<Match when={currentPanel() === 0}>
						<PanelFooter>
							<NewBoardButton onClick={() => setCurrentPanel(1)} />
							<Button
								type="button"
								variant="primary"
								symbol="bookmark_add"
								disabled={!selectedBoardId() || isSaving()}
								onClick={confirmSelected}
							>
								{props.confirmLabel}
							</Button>
						</PanelFooter>
					</Match>
					<Match when={currentPanel() === 1}>
						<PanelFooter>
							<Button
								type="button"
								variant="secondary"
								theme="monochrome"
								onClick={() => setCurrentPanel(0)}
							>
								Cancel
							</Button>
							<Button
								type="button"
								variant="primary"
								symbol="bookmark_add"
								disabled={isSaving()}
								onClick={createAndConfirm}
							>
								Create and {props.confirmLabel.toLowerCase()}
							</Button>
						</PanelFooter>
					</Match>
				</Switch>
			}
		>
			<Switch>
				<Match when={currentPanel() === 0}>
					<BoardList
						boards={boardsList}
						selectedBoardId={selectedBoardId}
						onSelectBoard={(boardId) => setSelectedBoardId(boardId)}
					/>
				</Match>
				<Match when={currentPanel() === 1}>
					<AddBoardForm
						errors={errors}
						formData={formData}
						setFormData={setFormData}
						showPrivacy={showPrivacy}
					/>
				</Match>
			</Switch>
		</Panel>
	);
};
